// Quiz Results Component - Display quiz attempt results
import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Quiz, QuizAttempt } from "../../../types/quiz";
import quizService from "../../../services/quizService";

interface QuizResultsProps {
  attempt?: QuizAttempt;
  quizId?: string; // Optional quizId for inline usage
  onBackToQuiz?: () => void; // Optional callback for inline usage
}

const QuizResults: React.FC<QuizResultsProps> = ({
  attempt: propAttempt,
  quizId: propQuizId,
  onBackToQuiz,
}) => {
  const { quizId: paramQuizId, attemptId } = useParams<{
    quizId: string;
    attemptId: string;
  }>();
  const quizId = propQuizId || paramQuizId;
  const location = useLocation();
  const queryClient = useQueryClient();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(
    propAttempt || null
  );
  const [allAttempts, setAllAttempts] = useState<QuizAttempt[]>([]);
  const [loading, setLoading] = useState(!propAttempt);
  const [unlockingModule, setUnlockingModule] = useState(false);

  // Helper function to format answer display based on question type
  const formatAnswer = (answer: any, question: any): string => {
    if (!answer) return "No answer";

    switch (question.type) {
      case "multiple_choice":
      case "true_false":
      case "short_answer":
      case "essay":
      case "numeric":
        return typeof answer === "string" ? answer : String(answer);

      case "multiple_select":
        return Array.isArray(answer) ? answer.join(", ") : String(answer);

      case "fill_blank":
        return Array.isArray(answer) ? answer.join(" / ") : String(answer);

      case "matching":
        if (typeof answer === "object" && answer !== null) {
          // Format as "Left Item -> Right Item" pairs
          const pairs = Object.entries(answer).map(([leftId, rightId]) => {
            const leftItem = question.leftColumn?.find(
              (item: any) => item.id === leftId
            );
            const rightItem = question.rightColumn?.find(
              (item: any) => item.id === rightId
            );
            return `${leftItem?.text || leftId} → ${
              rightItem?.text || rightId
            }`;
          });
          return pairs.join(", ");
        }
        return String(answer);

      case "ordering":
        if (Array.isArray(answer)) {
          return answer
            .map(
              (item: any, index: number) => `${index + 1}. ${item.item || item}`
            )
            .join(", ");
        }
        return String(answer);

      case "comprehension":
        if (typeof answer === "object" && answer !== null) {
          // Format sub-question answers
          const subAnswers = Object.entries(answer).map(
            ([subQId, subAnswer]) => {
              const subQuestion = question.subQuestions?.find(
                (sq: any) => sq._id === subQId
              );
              const formattedAnswer = formatAnswer(subAnswer, subQuestion);
              return `${subQuestion?.question || subQId}: ${formattedAnswer}`;
            }
          );
          return subAnswers.join("; ");
        }
        return String(answer);

      default:
        return typeof answer === "string" ? answer : JSON.stringify(answer);
    }
  };

  // Function to manually unlock the next module
  const unlockNextModule = async () => {
    if (!location.state?.courseId || !quizId) return;

    setUnlockingModule(true);
    try {
      // Call API to unlock next module
      const response = await fetch(
        `/api/elearning/courses/${location.state.courseId}/modules/${quizId}/unlock-next`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to unlock next module");
      }

      const data = await response.json();

      // Invalidate the course query to refresh the data
      await queryClient.invalidateQueries({
        queryKey: ["course-details", location.state.courseId],
      });

      alert(`✅ ${data.message}`);
    } catch (error) {
      console.error("Error unlocking next module:", error);
      alert("Failed to unlock next module. Please try again.");
    } finally {
      setUnlockingModule(false);
    }
  };

  // Helper function to check if quiz is currently available
  const isQuizAvailable = (quiz: Quiz): boolean => {
    const now = new Date();
    const { availableFrom, availableUntil } = quiz.schedule || {};

    if (availableFrom && now < new Date(availableFrom)) return false;
    if (availableUntil && now > new Date(availableUntil)) return false;

    return quiz.isPublished;
  };

  // Helper function to check if question review should be shown based on releaseReview setting
  const canShowQuestionReview = (quiz: Quiz, attempt: QuizAttempt): boolean => {
    if (!quiz.showCorrectAnswers) return false;

    const releaseReview = quiz.grading?.releaseReview || "immediately";
    const now = new Date();

    switch (releaseReview) {
      case "immediately":
        // Show review immediately after submission
        return true;

      case "after_quiz_closed":
        // Show review only after quiz availableUntil date has passed
        if (!quiz.schedule?.availableUntil) return true; // If no end date, show immediately
        return now > new Date(quiz.schedule.availableUntil);

      case "after_grading":
        // Show review only after the attempt has been graded (for essay questions)
        // Check if attempt status indicates grading is complete
        return (
          attempt.status === "submitted" ||
          attempt.status === "completed" ||
          (attempt.status !== "submitted_pending_grading" &&
            attempt.status !== "partially_graded")
        );

      case "never":
        // Never show the question review
        return false;

      default:
        return true;
    }
  };

  // Get message explaining why review is not available
  const getReviewUnavailableMessage = (quiz: Quiz): string => {
    const releaseReview = quiz.grading?.releaseReview || "immediately";

    switch (releaseReview) {
      case "after_quiz_closed":
        if (quiz.schedule?.availableUntil) {
          return `Question review will be available after ${new Date(
            quiz.schedule.availableUntil
          ).toLocaleString()}`;
        }
        return "Question review will be available after the quiz closes";

      case "after_grading":
        return "Question review will be available after your submission has been graded";

      case "never":
        return "Question review is not available for this quiz";

      default:
        return "Question review is not available yet";
    }
  };

  useEffect(() => {
    if (!propAttempt) {
      loadResults();
    } else {
      loadQuiz();
    }
  }, [quizId, attemptId, propAttempt]);

  // Invalidate course query if quiz was completed as part of a course
  useEffect(() => {
    if (
      attempt &&
      location.state?.courseId &&
      (attempt.status === "completed" ||
        attempt.status === "submitted" ||
        attempt.status === "submitted_pending_grading" ||
        attempt.status === "partially_graded")
    ) {
      // Invalidate the course data query to refresh quiz completion status
      queryClient.invalidateQueries({
        queryKey: ["course", location.state.courseId],
      });
    }
  }, [attempt, location.state?.courseId, queryClient]);

  const loadResults = async () => {
    try {
      setLoading(true);
      if (attemptId) {
        const attemptData = await quizService.getQuizAttempt(attemptId);
        setAttempt(attemptData);
        // Since attempt is populated with quiz, use it directly
        setQuiz(
          typeof attemptData.quizId === "object" ? attemptData.quizId : null
        );
      } else if (location.state?.attempt) {
        setAttempt(location.state.attempt);
        if (quizId) {
          const quizData = await quizService.getQuiz(quizId);
          setQuiz(quizData);
        }
      } else if (quizId) {
        // No attemptId or state, fetch student's attempts for this quiz
        const attempts = await quizService.getStudentAttempts(quizId);
        setAllAttempts(attempts);
        if (attempts.length > 0) {
          // Get the latest submitted attempt
          const latestAttempt = attempts
            .filter(
              (a) =>
                a.status === "submitted" ||
                a.status === "submitted_pending_grading" ||
                a.status === "partially_graded"
            )
            .sort(
              (a, b) =>
                new Date(b.submittedAt || b.completedAt || 0).getTime() -
                new Date(a.submittedAt || a.completedAt || 0).getTime()
            )[0];

          if (latestAttempt) {
            setAttempt(latestAttempt);
            const quizData = await quizService.getQuiz(quizId);
            setQuiz(quizData);
          } else {
            // No submitted attempts, show the quiz info anyway
            const quizData = await quizService.getQuiz(quizId);
            setQuiz(quizData);
          }
        } else {
          // No attempts, still load quiz
          const quizData = await quizService.getQuiz(quizId);
          setQuiz(quizData);
        }
      }
    } catch (error) {
      console.error("Error loading results:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuiz = async () => {
    if (quizId) {
      try {
        const quizData = await quizService.getQuiz(quizId);
        setQuiz(quizData);
      } catch (error) {
        console.error("Error loading quiz:", error);
      }
    }
  };

  const getScoreColor = (percentage: number): string => {
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (percentage: number): string => {
    if (percentage >= 80) return "bg-green-100";
    if (percentage >= 60) return "bg-yellow-100";
    return "bg-red-100";
  };

  const isPassed = (percentage: number): boolean => {
    return percentage >= (quiz?.passingScore || 60);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!attempt || !quiz) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Results Not Found
        </h3>
        <p className="text-gray-600">Unable to load quiz results.</p>
      </div>
    );
  }

  const percentage =
    attempt.percentageScore !== undefined
      ? attempt.percentageScore
      : attempt.percentage || 0;
  const isPendingGrading = attempt.status === "submitted_pending_grading";
  const passed = isPassed(percentage);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        {isPendingGrading ? (
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 mb-4">
            <span className="text-3xl">⏳</span>
          </div>
        ) : (
          <div
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${getScoreBgColor(
              percentage
            )} mb-4`}
          >
            <span className={`text-3xl font-bold ${getScoreColor(percentage)}`}>
              {Math.round(percentage)}%
            </span>
          </div>
        )}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{quiz.title}</h1>
        <div
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            isPendingGrading
              ? "bg-blue-100 text-blue-800"
              : passed
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {isPendingGrading
            ? "⏳ Submitted for Grading"
            : passed
            ? "✅ Passed"
            : "❌ Failed"}
        </div>
      </div>

      {/* Attempts List */}
      {allAttempts.length > 1 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Your Attempts
          </h2>
          <div className="space-y-3">
            {allAttempts
              .filter(
                (a) =>
                  a.status === "submitted" ||
                  a.status === "submitted_pending_grading" ||
                  a.status === "partially_graded" ||
                  a.status === "timed_out"
              )
              .sort(
                (a, b) =>
                  new Date(b.submittedAt || b.completedAt || 0).getTime() -
                  new Date(a.submittedAt || a.completedAt || 0).getTime()
              )
              .map((attemptItem) => (
                <div
                  key={attemptItem._id}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    attemptItem._id === attempt?._id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setAttempt(attemptItem)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          attemptItem.status === "submitted_pending_grading"
                            ? "bg-blue-500"
                            : attemptItem.status === "timed_out"
                            ? "bg-orange-500"
                            : attemptItem.percentageScore >= 60
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      ></div>
                      <div>
                        <p className="font-medium text-gray-900">
                          Attempt #{attemptItem.attempt || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          {attemptItem.submittedAt
                            ? new Date(
                                attemptItem.submittedAt
                              ).toLocaleDateString()
                            : attemptItem.completedAt
                            ? new Date(
                                attemptItem.completedAt
                              ).toLocaleDateString()
                            : "In Progress"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {attemptItem.status === "submitted_pending_grading" ? (
                        <span className="text-blue-600 font-medium">
                          Pending Grading
                        </span>
                      ) : attemptItem.status === "timed_out" ? (
                        <span className="text-orange-600 font-medium">
                          Timed Out -{" "}
                          {Math.round(attemptItem.percentageScore || 0)}%
                        </span>
                      ) : (
                        <span
                          className={`font-medium ${
                            (attemptItem.percentageScore || 0) >= 60
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {Math.round(attemptItem.percentageScore || 0)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Score Summary */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {isPendingGrading ? "Submission Status" : "Score Summary"}
        </h2>
        {isPendingGrading ? (
          <div className="text-center py-8">
            <div className="text-blue-600 text-4xl mb-4">📝</div>
            <p className="text-lg text-gray-700 mb-2">
              Your essay has been submitted successfully!
            </p>
            <p className="text-gray-600">
              Your teacher will review and grade your submission. You will be
              notified once grading is complete.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {attempt.totalScore !== undefined
                  ? attempt.totalScore
                  : attempt.score || 0}
              </div>
              <div className="text-sm text-gray-600">Points Earned</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {attempt.totalPossible !== undefined
                  ? attempt.totalPossible
                  : attempt.maxScore || 0}
              </div>
              <div className="text-sm text-gray-600">Total Points</div>
            </div>
            <div className="text-center">
              <div
                className={`text-2xl font-bold ${getScoreColor(percentage)}`}
              >
                {Math.round(percentage)}%
              </div>
              <div className="text-sm text-gray-600">Percentage</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {quiz.passingScore}%
              </div>
              <div className="text-sm text-gray-600">Required to Pass</div>
            </div>
          </div>
        )}
      </div>

      {/* Attempt Details */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Attempt Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-gray-600">
              Started At:
            </span>
            <div className="text-gray-900">
              {new Date(attempt.startedAt).toLocaleString()}
            </div>
          </div>
          {attempt.completedAt && (
            <div>
              <span className="text-sm font-medium text-gray-600">
                Completed At:
              </span>
              <div className="text-gray-900">
                {new Date(attempt.completedAt).toLocaleString()}
              </div>
            </div>
          )}
          <div>
            <span className="text-sm font-medium text-gray-600">
              Time Limit:
            </span>
            <div className="text-gray-900">{quiz.timeLimit} minutes</div>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-600">
              Questions:
            </span>
            <div className="text-gray-900">
              {quiz.questions.length} questions
            </div>
          </div>
        </div>
      </div>

      {/* Feedback */}
      {attempt.feedback && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Feedback</h2>
          <div className="prose prose-sm text-gray-700">{attempt.feedback}</div>
          {attempt.gradedBy && attempt.gradedAt && (
            <div className="mt-4 text-xs text-gray-500">
              Graded by {attempt.gradedBy} on{" "}
              {new Date(attempt.gradedAt).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Question Review */}
      {quiz && canShowQuestionReview(quiz, attempt) && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Question Review
          </h2>
          <div className="space-y-6">
            {quiz.questions.map((question, index) => {
              const userAnswer = attempt.answers.find(
                (a) =>
                  a.questionId === question._id ||
                  a.questionId?.toString() === question._id
              );
              const isCorrect = userAnswer?.isCorrect;
              const needsManualGrading = userAnswer?.needsManualGrading;
              const isManuallyGraded =
                userAnswer?.gradedAt && !needsManualGrading;
              const pointsEarned = isManuallyGraded
                ? userAnswer?.score || 0
                : userAnswer?.pointsEarned || 0;

              return (
                <div
                  key={question._id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-medium text-gray-900">
                      Question {index + 1}: {(question as any).question}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        {pointsEarned} / {question.points} pts
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          needsManualGrading
                            ? "bg-yellow-100 text-yellow-800"
                            : isManuallyGraded
                            ? "bg-blue-100 text-blue-800"
                            : isCorrect
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {needsManualGrading
                          ? "Pending Review"
                          : isManuallyGraded
                          ? "✓ Graded"
                          : isCorrect
                          ? "✓ Correct"
                          : "✗ Incorrect"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {question.type === "comprehension" ? (
                      <div>
                        <span className="font-medium text-gray-600">
                          Your Answers:
                        </span>
                        <div className="mt-2 space-y-2">
                          {(question as any).subQuestions?.map(
                            (subQ: any, subIndex: number) => {
                              const subAnswer = (userAnswer?.answer as any)?.[
                                subQ._id
                              ];
                              const subCorrect =
                                subAnswer &&
                                formatAnswer(subAnswer, subQ) ===
                                  formatAnswer(subQ.correctAnswer, subQ);
                              return (
                                <div key={subQ._id} className="ml-4">
                                  <div className="text-gray-700">
                                    <strong>{subIndex + 1}.</strong>{" "}
                                    {subQ.question}
                                  </div>
                                  <div className="ml-4 mt-1">
                                    <span
                                      className={
                                        needsManualGrading
                                          ? "text-gray-700"
                                          : isManuallyGraded
                                          ? "text-blue-700"
                                          : subCorrect
                                          ? "text-green-700"
                                          : "text-red-700"
                                      }
                                    >
                                      {formatAnswer(subAnswer, subQ)}
                                    </span>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span className="font-medium text-gray-600">
                          Your Answer:{" "}
                        </span>
                        <span
                          className={
                            needsManualGrading
                              ? "text-gray-700"
                              : isManuallyGraded
                              ? "text-blue-700"
                              : isCorrect
                              ? "text-green-700"
                              : "text-red-700"
                          }
                        >
                          {formatAnswer(userAnswer?.answer, question)}
                        </span>
                      </div>
                    )}

                    {isManuallyGraded && userAnswer?.feedback && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-md">
                        <span className="font-medium text-blue-900">
                          Teacher Feedback:{" "}
                        </span>
                        <span className="text-blue-800">
                          {userAnswer.feedback}
                        </span>
                      </div>
                    )}

                    {!needsManualGrading &&
                      !isManuallyGraded &&
                      !isCorrect &&
                      (question.type === "comprehension"
                        ? (question as any).subQuestions?.some(
                            (sq: any) => sq.correctAnswer
                          )
                        : (question as any).correctAnswer) && (
                        <div>
                          {question.type === "comprehension" ? (
                            <div>
                              <span className="font-medium text-gray-600">
                                Correct Answers:
                              </span>
                              <div className="mt-2 space-y-2">
                                {(question as any).subQuestions?.map(
                                  (subQ: any, subIndex: number) => {
                                    if (!subQ.correctAnswer) return null;
                                    return (
                                      <div key={subQ._id} className="ml-4">
                                        <div className="text-gray-700">
                                          <strong>{subIndex + 1}.</strong>{" "}
                                          {subQ.question}
                                        </div>
                                        <div className="ml-4 mt-1">
                                          <span className="text-green-700">
                                            {formatAnswer(
                                              subQ.correctAnswer,
                                              subQ
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <span className="font-medium text-gray-600">
                                Correct Answer:{" "}
                              </span>
                              <span className="text-green-700">
                                {formatAnswer(
                                  (question as any).correctAnswer,
                                  question
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                    {(question as any).explanation && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-md">
                        <span className="font-medium text-blue-900">
                          Explanation:{" "}
                        </span>
                        <span className="text-blue-800">
                          {(question as any).explanation}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Question Review Unavailable Message */}
      {quiz &&
        quiz.showCorrectAnswers &&
        !canShowQuestionReview(quiz, attempt) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Question Review Unavailable
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{getReviewUnavailableMessage(quiz)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Actions */}
      <div className="flex justify-center space-x-4">
        {location.state?.courseId && passed && (
          <button
            onClick={unlockNextModule}
            disabled={unlockingModule}
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {unlockingModule ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Unlocking...
              </>
            ) : (
              <>🔓 Unlock Next Module</>
            )}
          </button>
        )}
        {location.state?.courseId && (
          <Link
            to={`/student/elearning/course/${location.state.courseId}/continue`}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Back to Course
          </Link>
        )}
        <Link
          to="/student/elearning/quizzes"
          className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
        >
          Back to Quizzes
        </Link>

        {quiz.attempts &&
          quiz.attempts > 1 &&
          attempt.status === "completed" &&
          !passed && (
            <Link
              to={`/student/elearning/quizzes/${quiz._id}`}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Retry Quiz
            </Link>
          )}
      </div>
    </div>
  );
};

export default QuizResults;
